import { defineStore } from 'pinia';
import { isFocusedSyntheticObservation } from '@shared/assessment-definition/dist/src/focused-plan';
import type {
  ClinicalNoteState,
  TextModeExtraction,
  TextModeObservation,
} from '../types/text-mode.types';

export interface NoteState {
  clinicalNote: ClinicalNoteState;
  originalExtractions: TextModeExtraction[];
  originalObservations: TextModeObservation[];
  focusedAssessments: string[];
}

function createInitialNoteState(): ClinicalNoteState {
  return {
    text: '',
    submitted: false,
    isSubmitting: false,
  };
}

export const useNoteStore = defineStore('textModeNote', {
  state: (): NoteState => ({
    clinicalNote: createInitialNoteState(),
    originalExtractions: [],
    originalObservations: [],
    focusedAssessments: [],
  }),

  getters: {
    observationsForConversion: (state) => state.originalObservations
      .filter(obs => !isFocusedSyntheticObservation(obs.name))
      .map(obs => ({
        type: 'response',
        name: obs.name,
        value: String(obs.value)
      })),
  },

  actions: {
    setText(text: string): void {
      this.clinicalNote.text = text;
    },

    setSubmitting(isSubmitting: boolean): void {
      this.clinicalNote.isSubmitting = isSubmitting;
    },

    setSubmitted(submitted: boolean): void {
      this.clinicalNote.submitted = submitted;
    },

    setOriginalExtractions(extractions: TextModeExtraction[] | null | undefined): void {
      this.originalExtractions = extractions ?? [];
    },

    setOriginalObservations(observations: TextModeObservation[] | null | undefined): void {
      this.originalObservations = observations ?? [];
    },

    setFocusedAssessments(assessments: string[] | null | undefined): void {
      this.focusedAssessments = assessments ?? [];
    },

    resume(payload: NoteState): void {
      Object.assign(this, {
        ...payload,
        clinicalNote: { ...payload.clinicalNote, isSubmitting: false },
      });
    },

    reset(): void {
      this.clinicalNote = createInitialNoteState();
      this.originalExtractions = [];
      this.originalObservations = [];
      this.focusedAssessments = [];
    },
  },
});

